import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SearchInput } from '@/components/SearchInput';
import { ResultCard } from '@/components/ResultCard';
import { useAppDispatch, useAppSelector } from '@/store';
import { resetSearch, runSearch } from '@/store/slices/searchSlice';
import type { SearchType } from '@/types/search';

function isSearchType(value: string | null): value is SearchType {
  return value === 'multi' || value === 'movie' || value === 'tv';
}

export function ResultsPage(): JSX.Element {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();

  const q = params.get('q')?.trim() ?? '';
  const typeParam = params.get('type');
  const type: SearchType = isSearchType(typeParam) ? typeParam : 'multi';

  const { status, query, results, totalResults, error, errorCode } = useAppSelector((s) => s.search);

  // Fetch whenever the query string changes. Abort any in-flight request on
  // unmount or before kicking off a new one — otherwise a slow earlier
  // response can clobber a faster newer one.
  useEffect(() => {
    if (!q) {
      dispatch(resetSearch());
      return;
    }
    const promise = dispatch(runSearch({ query: q, type }));
    return () => { promise.abort(); };
  }, [q, type, dispatch]);

  const submit = (next: string): void => {
    setParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('q', next);
      return p;
    });
  };

  const switchType = (next: SearchType): void => {
    setParams((prev) => {
      const p = new URLSearchParams(prev);
      if (next === 'multi') p.delete('type');
      else p.set('type', next);
      return p;
    });
  };

  return (
    <main className="results-page">
      <header className="results-page__header">
        <SearchInput
          initialValue={q}
          placeholder={t('search.placeholder')}
          onSubmit={submit}
          autoFocus={!q}
        />

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div role="group" aria-label={t('search.filterByType')} style={{ display: 'flex', gap: 6 }}>
            {(['multi', 'movie', 'tv'] as const).map((typeOption) => (
              <button
                key={typeOption}
                type="button"
                aria-pressed={type === typeOption}
                className={type === typeOption ? 'btn' : 'btn btn-outline'}
                style={{ padding: '8px 14px', fontSize: '0.85rem' }}
                onClick={() => switchType(typeOption)}
              >
                {typeOption === 'multi' ? t('search.typeAll') : typeOption === 'movie' ? t('search.typeMovies') : t('search.typeSeries')}
              </button>
            ))}
          </div>
          {status === 'success' && q && (
            <div className="results-page__meta" aria-live="polite">
              {t('search.resultCount', { count: totalResults, formattedCount: totalResults.toLocaleString(), query })}
            </div>
          )}
        </div>
      </header>

      {!q && (
        <div className="results-empty">{t('search.emptyStart')}</div>
      )}

      {q && status === 'loading' && (
        <div className="results-loading" role="status">{t('search.searching')}</div>
      )}

      {q && status === 'error' && (
        <div className="results-error" role="alert">
          {errorCode === 'TMDB_NOT_CONFIGURED'
            ? t('search.errorNotConfigured')
            : errorCode === 'UNAUTHORIZED'
              ? t('search.errorUnauthorized')
              : error ?? t('search.errorGeneric')}
          {errorCode === 'UNAUTHORIZED' && (
            <div style={{ marginTop: 12 }}>
              <button
                type="button"
                className="btn"
                onClick={() => {
                  // Canonical from-shape (pathname / search), matching ProtectedRoute.
                  // Preserves type filter on the way back.
                  const search = new URLSearchParams({ q });
                  if (type !== 'multi') search.set('type', type);
                  navigate('/login', {
                    state: { from: { pathname: '/results', search: `?${search.toString()}` } },
                  });
                }}
              >
                {t('search.signIn')}
              </button>
            </div>
          )}
        </div>
      )}

      {q && status === 'success' && results.length === 0 && (
        <div className="results-empty">{t('search.noResults', { query: q })}</div>
      )}

      {q && status === 'success' && results.length > 0 && (
        <div className="results-grid">
          {results.map((r) => (
            <ResultCard key={`${r.mediaType}-${r.id}`} result={r} />
          ))}
        </div>
      )}
    </main>
  );
}
